import * as d3 from 'd3';

import * as CodonTables from 'biojs/Codons';
import {StandardAminoAcidSLA} from 'biojs/Codons';
import CODON_USAGE_DATA from 'data/codonUsage.json';
import {Organism} from 'molbio/codon-optimization/CodonOptimizationConstants';
import {
  EXTENDED_AMINO_ACID_COLORINGS,
  ProteinColorScheme,
} from 'molbio/proteins/util/ProteinColoringConstants';
import {GRAY} from 'ui-library/Colors';
import sortedBy from 'util/sortedBy';

enum OrganismTier {
  Common = 1,
  Uncommon = 2,
}

export type OrganismCodonUsage = {
  _source: string;
  _displayName?: string;
  _tier: OrganismTier;
  _apiIdentifier: string;
} & {
  [aminoAcid in StandardAminoAcidSLA]: {
    [codon: string]: {
      fraction: number;
      frequency: number;
    };
  };
};

type CodonUsageData = {[organism in Organism]: OrganismCodonUsage};

export const CODON_USAGE: CodonUsageData = CODON_USAGE_DATA;

export interface BasicCodon {
  TLA: string;
  SLA: StandardAminoAcidSLA;
  color: string;
  bases: string;
}
export type Codon = BasicCodon & {
  fraction: number | null;
  frequency: number | null;
};

const hexToHalfOpacityRGB = (hexColor: string): string => {
  const {r, g, b} = d3.rgb(hexColor);
  return `rgba(${r}, ${g}, ${b}, 0.5)`;
};

export const getCodonsForOrganism = (
  organism: Organism | null,
  colorScheme: ProteinColorScheme
): Array<Codon> =>
  Object.entries(CodonTables.STANDARD_DNA_CODE).map(([bases, SLA]) => {
    const color =
      colorScheme === ProteinColorScheme.NONE
        ? GRAY
        : EXTENDED_AMINO_ACID_COLORINGS[colorScheme][SLA].backgroundColor;
    return {
      bases,
      SLA,
      color: hexToHalfOpacityRGB(color),
      TLA: CodonTables.AMINO_ACID_TLA[SLA],
      fraction: organism && CODON_USAGE[organism][SLA][bases].fraction,
      frequency: organism && CODON_USAGE[organism][SLA][bases].frequency,
    };
  });

export const getSourceForOrganism = (organism: Organism): string => CODON_USAGE[organism]._source;
export const getDisplayNameForOrganism = (organism: Organism): string =>
  CODON_USAGE[organism]._displayName || organism;

export const COMMON_ORGANISMS = sortedBy(Object.keys(CODON_USAGE), [
  (organism: Organism) => getDisplayNameForOrganism(organism),
]).filter((organism: Organism) => CODON_USAGE[organism]._tier === OrganismTier.Common) as Array<Organism>;
export const UNCOMMON_ORGANISMS = sortedBy(Object.keys(CODON_USAGE), [
  (organism: Organism) => getDisplayNameForOrganism(organism),
]).filter((organism: Organism) => CODON_USAGE[organism]._tier === OrganismTier.Uncommon) as Array<Organism>;

export const ORGANISMS = COMMON_ORGANISMS.concat(UNCOMMON_ORGANISMS);
